import React, { useEffect, useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import { scoreKindLabel } from '../ds';
import { inSentence } from '../ds/Composer';
import { uploadLine, stepWords, spectrumLean, CHECKS } from '../../utils/listeningLines';
import { FULL_MIX } from '../../utils/instruments';

const BANDS = 32;
// Half a waveform bar at full level, in px: each channel gets half the panel's 120px inner height
const HALF_BAR = 50;

const SIZE = 128;
const STROKE = 10;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const clock = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

/** Spectrum bars shaped like the instrument (taller where it lives), each bouncing at its own pace */
const useBands = (lean) => useMemo(() => Array.from({ length: BANDS }, (_, i) => {
  const position = i / (BANDS - 1);
  const closeness = 1 - Math.min(1, Math.abs(position - lean) * 1.6);
  return {
    height: `${Math.round(30 + closeness * 66)}px`,
    duration: `${(0.8 + ((i * 7) % 5) * 0.15).toFixed(2)}s`,
    delay: `${(-((i * 13) % 10) * 0.11).toFixed(2)}s`,
  };
}), [lean]);

/** The stand-in when the browser can't read the clip (or hasn't yet): bars bouncing like an analyzer */
const Spectrum = ({ lean }) => {
  const bands = useBands(lean);
  return (
    <div className="absolute inset-x-space-5 bottom-0 top-space-4 flex items-end gap-1.5">
      {bands.map((band, i) => (
        <span
          key={i}
          data-testid="analyzing-band"
          className="flex-1 rounded-t-[4px] bg-amber origin-bottom animate-eq motion-reduce:animate-none"
          style={{ height: band.height, animationDuration: band.duration, animationDelay: band.delay }}
        />
      ))}
    </div>
  );
};

/**
 * One layer of the clip's waveform: left channel above the line, right below. Bars past `sent` (a
 * 0-1 share of the file) lie flat, and spring up as the upload reaches them.
 */
const WaveformBars = ({ waveform, sent = 1, color, testId, className = '' }) => (
  // Every other bar on a phone, so the bars stay wider than the gaps
  <div data-testid={testId} className={`absolute inset-space-4 flex items-stretch gap-[2px] max-sm:[&>*:nth-child(even)]:hidden ${className}`}>
    {waveform.left.map((left, i) => {
      const reached = i / waveform.left.length < sent;
      const height = (level) => `${Math.max(2, Math.round((reached ? level : 0) * HALF_BAR))}px`;
      const bar = `rounded-[2px] transition-[height] duration-300 ease-spring motion-reduce:transition-none ${reached ? color : 'bg-line'}`;
      return (
        <span key={i} className="flex-1 min-w-0 flex flex-col justify-center">
          <span data-testid={`${testId}-left`} className={bar} style={{ height: height(left) }} />
          <span className={bar} style={{ height: height(waveform.right[i]) }} />
        </span>
      );
    })}
  </div>
);

/**
 * The server's checks: waiting, then listening while it measures, then done with what each found.
 * `checks` holds the finished ones' findings by id.
 */
const CheckChips = ({ checks = {}, measuring }) => (
  <ul aria-label="Checks" className="m-0 p-0 list-none grid grid-cols-2 sm:grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-space-2">
    {CHECKS.map(({ id, label }) => {
      const found = checks[id];
      const state = found ? 'done' : measuring ? 'active' : 'waiting';
      return (
        <li
          key={id}
          data-testid={`check-${id}`}
          data-state={state}
          className={`min-h-[56px] px-space-3 py-[10px] flex items-center gap-[10px] rounded-radius-md border ${
            found ? 'bg-raised border-raised animate-pop motion-reduce:animate-none' : measuring ? 'border-amber' : 'border-line'
          }`}
        >
          {found ? (
            <span aria-hidden="true" className="w-[22px] h-[22px] flex-none inline-flex items-center justify-center rounded-full bg-amber text-on-amber">
              <Check size={14} strokeWidth={3.5} />
            </span>
          ) : measuring ? (
            <span aria-hidden="true" className="w-[22px] h-4 flex-none flex items-end justify-center gap-[2px]">
              {['0s', '-0.3s', '-0.55s'].map((delay) => (
                <span key={delay} className="w-[3px] h-4 rounded-[2px] bg-amber origin-bottom animate-level motion-reduce:animate-none" style={{ animationDelay: delay }} />
              ))}
            </span>
          ) : (
            <span aria-hidden="true" className="w-[18px] h-[18px] m-[2px] flex-none rounded-full border-2 border-line" />
          )}
          <span className="min-w-0 flex flex-col">
            <span className={`text-small font-bold ${found || measuring ? 'text-ink' : 'text-muted'}`}>{label}</span>
            <span className="text-caption text-muted">{found || (measuring ? 'listening…' : 'waiting')}</span>
          </span>
        </li>
      );
    })}
  </ul>
);

/**
 * Takes the analysis card's place while a version is analyzed. While the file uploads, the ring shows
 * how much has gone and the clip's own waveform rises as it's sent; then the ring's arc sweeps around
 * a timer and a playhead plays through the waveform, lighting it amber. `info` is the analysis:
 * { version, startedAt, filename, name, category, instrumentType } and, as the store fills them in,
 * { sent, total, uploadedAt }, `waveform` (null when the browser can't read the file, which shows
 * bars shaped like the instrument instead), and the server's `step` and `checks` (see analysisStore):
 * opening the file, measuring (the check chips tick off), then writing it up (the waveform dims).
 * Nothing moves under reduced motion; the words still change.
 */
export const AnalyzingCard = ({ info }) => {
  const { version, startedAt = Date.now(), filename, name, category, instrumentType, sent, total, uploadedAt, waveform, step, focus, checks } = info;
  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const uploading = Boolean(total) && !uploadedAt;
  const share = uploading ? sent / total : 1;
  const seconds = Math.max(0, Math.floor((now - (uploadedAt || startedAt)) / 1000));
  const isMix = category === FULL_MIX;
  const what = isMix ? 'full mix' : inSentence(name || 'clip');
  const words = stepWords(step, { what, filename, category, seconds, focus });
  const writing = step === 'write';

  return (
    <article aria-label={`V${version} analysis in progress`} aria-busy="true" className="flex flex-col gap-space-7 p-space-6 sm:p-space-8 rounded-radius-xl bg-surface font-text">
      <div className="flex flex-wrap items-center gap-space-6">
        <div aria-hidden="true" className="relative w-score-ring h-score-ring flex-none">
          <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
            <circle cx={SIZE / 2} cy={SIZE / 2} r={RADIUS} fill="none" className="stroke-line" strokeWidth={STROKE} />
            {uploading ? (
              <circle
                data-testid="upload-arc"
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                className="stroke-amber origin-center -rotate-90 transition-[stroke-dasharray] duration-motion motion-reduce:transition-none"
                strokeWidth={STROKE}
                strokeLinecap="round"
                strokeDasharray={`${CIRCUMFERENCE * share} ${CIRCUMFERENCE}`}
              />
            ) : (
              <circle
                data-testid="analyzing-arc"
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                className="stroke-amber origin-center animate-sweep motion-reduce:animate-none"
                strokeWidth={STROKE}
                strokeLinecap="round"
                strokeDasharray={`${CIRCUMFERENCE * 0.19} ${CIRCUMFERENCE}`}
              />
            )}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
            <span className="font-mono text-data-lg text-ink">{uploading ? `${Math.round(share * 100)}%` : clock(seconds)}</span>
            <span className="text-caption text-muted">{uploading ? 'sending' : words.ring}</span>
          </div>
        </div>
        <div className="flex-[1_1_260px] min-w-0 flex flex-col gap-space-2">
          <span className="text-label text-muted">{scoreKindLabel(isMix ? 'mastering_readiness' : 'stem_health')} · {uploading ? 'uploading' : 'analyzing'} V{version}</span>
          <h2 className="m-0 font-display text-title-1 text-ink break-words">{uploading ? `Packing up your ${what}` : words.title}</h2>
          <p role="status" aria-live="polite" className="m-0 min-h-[53px] text-lead text-ink-soft break-words">
            {uploading ? uploadLine(filename, sent, total) : words.line}
          </p>
        </div>
      </div>

      <div aria-hidden="true" className="relative h-[152px] rounded-radius-md bg-base overflow-hidden">
        <div data-testid="waveform-panel" className={`absolute inset-0 transition-opacity duration-300 ${writing ? 'opacity-30' : ''}`}>
          {!waveform ? (
            <Spectrum lean={spectrumLean(category, instrumentType)} />
          ) : uploading ? (
            <WaveformBars waveform={waveform} sent={share} color="bg-control" testId="waveform" />
          ) : writing ? (
            <WaveformBars waveform={waveform} color="bg-amber" testId="waveform" />
          ) : (
            <>
              <WaveformBars waveform={waveform} color="bg-control" testId="waveform" />
              <WaveformBars waveform={waveform} color="bg-amber" testId="waveform-lit" className="animate-listen motion-reduce:animate-none" />
              <div data-testid="playhead" className="absolute inset-y-space-2 inset-x-space-4 motion-reduce:hidden">
                <span className="absolute inset-y-0 w-0.5 -ml-px rounded-full bg-ink animate-playhead" />
              </div>
            </>
          )}
        </div>
        {writing && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="inline-flex items-center gap-[10px] px-[18px] py-[10px] rounded-radius-pill bg-raised text-small font-bold text-ink">
              Writing it up
              <span className="inline-flex gap-space-1">
                {['0s', '0.15s', '0.3s'].map((delay) => (
                  <span key={delay} className="w-1.5 h-1.5 rounded-full bg-amber animate-typing motion-reduce:animate-none" style={{ animationDelay: delay }} />
                ))}
              </span>
            </span>
          </div>
        )}
      </div>

      {step && <CheckChips checks={checks} measuring={step === 'measure'} />}

      <span className="text-caption text-muted">
        {uploading
          ? 'Keep this tab open until it’s sent. It only takes a few seconds.'
          : 'This usually takes under a minute. You can open another clip in the meantime; this one keeps going.'}
      </span>
    </article>
  );
};
